/**
 * @krisspy-file
 * @type page
 * @name "AuthCallbackPage"
 * @title "Ověření účtu"
 * @description "Callback po kliknutí v potvrzovacím emailu - zachytí session a přesměruje uživatele"
 * @routes ["/auth/callback"]
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

type Status = 'loading' | 'success' | 'error';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!isSupabaseConfigured) {
      // V mock režimu rovnou na výběr cesty
      navigate('/journey-select', { replace: true });
      return;
    }

    let cancelled = false;

    const finish = async () => {
      // Supabase JS klient s detectSessionInUrl: true automaticky parsne hash
      // (#access_token=...&refresh_token=...) a uloží session. Stačí počkat
      // a přečíst session.
      try {
        // Krátká pauza aby supabase-js stihl zpracovat URL hash
        await new Promise(r => setTimeout(r, 150));

        const { data: { session }, error } = await supabase.auth.getSession();
        if (cancelled) return;

        if (error) {
          setStatus('error');
          setErrorMsg(error.message);
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        if (session?.user) {
          setStatus('success');
          const isAdmin =
            session.user.user_metadata?.role === 'admin' ||
            (session.user.app_metadata as any)?.role === 'admin';
          // Vyčistíme hash z URL (čistá historie)
          window.history.replaceState(null, '', window.location.pathname);
          setTimeout(() => {
            navigate(isAdmin ? '/admin' : '/journey-select', { replace: true });
          }, 1200);
        } else {
          // Žádná session - link byl pravděpodobně zneplatněn nebo už použit
          setStatus('error');
          setErrorMsg('Odkaz vypršel nebo již byl použit. Přihlaste se prosím ručně.');
          setTimeout(() => navigate('/login', { replace: true }), 3000);
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err?.message ?? 'Něco se pokazilo');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    finish();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl shadow-lg mb-6">
          <Map className="w-8 h-8 text-white" />
        </div>

        {status === 'loading' && (
          <>
            <div className="w-10 h-10 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Ověřuji účet...</h2>
            <p className="text-sm text-gray-500">Chvilku strpení, hned vás přesměrujeme.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Účet potvrzen</h2>
            <p className="text-sm text-gray-500">Přesměrovávám vás do aplikace...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Něco se pokazilo</h2>
            <p className="text-sm text-gray-500 mb-2">{errorMsg}</p>
            <p className="text-xs text-gray-400">Přesměrovávám na přihlášení...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
